import { Maybe, Study } from '@csp/csp-common-model';
import { DmdpAuthService } from '@csp/csp-fe-auth';
import { WebHcpNavigationHeaderTopBarBlock, WebHcpNavigationTabsTabsBlock } from '@csp/csp-web-content-definitions';
import { useEffect } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { CspPath, DEFAULT_AUTHENTICATED_PATH } from '../../../CspPath';
import { useStudiesContext } from '../../../common/context/studies/useStudiesContext';
import { useStudyContext } from '../../../common/context/study/useStudyContext';
import { StaticContentService } from '../../../common/service/StaticContentService';
import { useInformedConsentIsEnabled } from '../../informed-consent/hook/useInformedConsentIsEnabled';

type Texts = {
  topBar: WebHcpNavigationHeaderTopBarBlock;
  tabs: WebHcpNavigationTabsTabsBlock;
};

type Out = {
  goBack(): void;
  study: Study;
  texts: Texts;
  hasMultipleStudies: Maybe<boolean>;
};

export const useMainNavPages = (): Out => {
  const { clearSelectedStudy, hasMultipleStudies } = useStudiesContext();
  const { study } = useStudyContext();

  const topBarBlock = StaticContentService.getPublicBlock('webHcpNavigationHeader', 'topBar');
  const tabsBlock = StaticContentService.getPublicBlock('webHcpNavigationTabs', 'tabs');

  const isAtInformedConsentPath = !!useMatch(CspPath.INFORMED_CONSENT);
  const isAtInformedConsentMasterPath = !!useMatch(CspPath.INFORMED_CONSENT_LIST_MASTER);
  const isAtInformedConsentLocalPath = !!useMatch(CspPath.INFORMED_CONSENT_LIST_LOCAL);
  const isAtInformedConsentSitePath = !!useMatch(CspPath.INFORMED_CONSENT_LIST_SITE);

  const informedConsentIsEnabled = useInformedConsentIsEnabled();
  const isAtAnyInformedConsentPath =
    isAtInformedConsentPath ||
    isAtInformedConsentMasterPath ||
    isAtInformedConsentLocalPath ||
    isAtInformedConsentSitePath;
  const shouldNavigateToDefaultPath = isAtAnyInformedConsentPath && !informedConsentIsEnabled;

  const navigate = useNavigate();

  const goBack = (): void => {
    DmdpAuthService.revokeToken().catch(e => console.warn('Failed running DmdpAuthService.revokeToken', e));
    clearSelectedStudy();
    navigate(CspPath.STUDIES);
  };

  useEffect(() => {
    if (shouldNavigateToDefaultPath) {
      navigate(DEFAULT_AUTHENTICATED_PATH, { replace: true });
    }
  }, [shouldNavigateToDefaultPath, navigate]);

  return {
    goBack,
    study,
    texts: {
      topBar: topBarBlock,
      tabs: tabsBlock,
    },
    hasMultipleStudies,
  };
};
